import { prefersReducedMotion } from './motion/prefersReducedMotion.js';

export class FloatingIcon extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    // Defer init until layout has settled. Defer scripts (including main.js)
    // run BEFORE DOMContentLoaded, so a synchronous connectedCallback during
    // upgrade can fire before the first layout pass — ScrollTrigger then
    // captures stale measurements and the scroll-fly never scrubs.
    //
    // For initial load: wait for DOMContentLoaded.
    // For dynamic re-renders (theme editor section refresh, etc., where DCL
    // has already fired): wait one animation frame so any pending layout
    // settles before ScrollTrigger measures.
    const run = () => this.init();
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', run, { once: true });
    } else {
      requestAnimationFrame(run);
    }
  }


  init() {
    const scaleYProp = '--tw-scale-y'
    const scaleXProp = '--tw-scale-x'
    const translateYProp = '--tw-translate-y'
    const container = this.closest('.block-floating-icons') || this.closest('.shopify-section');
    const sectionContainer = this.closest('.shopify-section');

    //This function sets the start of the scroll animation to the current start of the window + 100vh. It can be called as a callback to another animation, which is perfect.
    const parallaxIcon = (icon) => {
      const curTop = window.scrollY;
      let scrollTl = gsap.timeline({
        scrollTrigger: {
          trigger: "body",
          start: `${curTop}px top`,
          end: `${curTop + window.innerHeight}px top`,
          scrub: true,
        },
      });

      scrollTl.fromTo(
        icon,
        {
          [translateYProp]: `0%`,
        },
        {
          [translateYProp]: `50%`,
        },
        0
      );
    };

    const fallInIcon = (icon) => {
      return new Promise((resolve, reject) => {
        gsap.fromTo(
          icon,
          {
            opacity: 0,
            [translateYProp]: `-100%`,
          },
          {
            delay: 0.5,
            opacity: 1,
            [translateYProp]: `0%`,
            duration: 1.2,
            onComplete: resolve,
          }
        );
      });
    };

    const scaleIcon = (icon) => {
      const curTop = window.scrollY;
      let scrollTl = gsap.timeline({
        // scrollTrigger: {
        //   trigger: "body",
        //   start: `${curTop} top`,
        //   end: `${curTop + window.innerHeight} top`,
        //   scrub: true,
        // },
        duration: 1,
        delay: 0.3,
      });

      scrollTl.fromTo(
        icon,
        {
          opacity: 1,
          scale: 0.9,
        },
        {
          opacity: 1,
          scale: 1.3,
        },
        0
      );

      // TODO: This is def the wrong spot for this. We need a separate file for the "hitchcock-effect" screen as a whole. Multiple items in here would blow this up.
      const containerHeader = container?.querySelector('.block-header-region');
      if (containerHeader) {
        scrollTl.fromTo(
          containerHeader,
          {
            y: '50px',
            scale: 1,
          },
          {
            y: 0,
            scale: 0.9,
          },
          0
        );
      }

      const containerFooter = container?.querySelector('.block-footer-region');
      if (containerFooter) {
        scrollTl.fromTo(
          containerFooter,
          {
            y: '-50px',
            scale: 1,
          },
          {
            y: 0,
            scale: 0.9,
          },
          0
        );
      }
    };

    const popIcon = (icon) => {
      return new Promise((resolve, reject) => {
        let scrollTl = gsap.timeline({
          scrollTrigger: {
            trigger: icon.closest('.shopify-section'),
          },
        });

        scrollTl.fromTo(
          icon,
          {
            opacity: 0,
            [scaleYProp]: 0,
            [scaleXProp]: 0,
          },
          {
            delay: 0.25 + (Math.random() * 1.25),
            opacity: 1,
            [scaleYProp]: 1,
            [scaleXProp]: 1,
            ease: "bounce.out",
            duration: 0.4,
            onComplete: resolve,
          }
        );
      });
    }

    // Match the inner `.floating-object-hero` element (added by the section's
    // liquid markup itself) instead of relying on the schema `class` reaching
    // the Shopify wrapper — that path was unreliable.
    const floatingObjectHero = this.closest('.floating-object-hero');

    if (container?.classList.contains('hitchcock-effect')) {
      scaleIcon(this);
    }
    else if (floatingObjectHero) {
      this.runHeroAnimation(floatingObjectHero);
    }
    else {
      fallInIcon(this).then(() => parallaxIcon(this));
    }
  }

  /*
   * Floating-object-hero treatment.
   *  - Fade in on load (overrides the .opacity-0 baseline).
   *  - Continuous idle bob/rotate on the inner `.floating-icon__idle` wrapper.
   *  - Scroll-jacked fly-away on the inner `.floating-icon__fly` wrapper as
   *    the hero section scrolls past the viewport top.
   * Two separate inner wrappers so the idle loop and the scrub'd scroll
   * timeline never fight over the same transform.
   */
  runHeroAnimation(sectionContainer) {
    // Reveal immediately — the `.opacity-0` baseline is only there to hide
    // chips during the parallax entrance used in other sections. In the hero
    // we want them on-screen right away so the idle loop is visible.
    gsap.set(this, { opacity: 1 });

    if (prefersReducedMotion()) return;

    const idleEl = this.querySelector('.floating-icon__idle');
    const flyEl = this.querySelector('.floating-icon__fly');

    if (idleEl) this.startIdleLoop(idleEl);
    if (flyEl) this.startScrollFly(flyEl, sectionContainer);
  }

  startIdleLoop(el) {
    // Per-chip randomization so the row of chips never feels synchronized.
    const yAmp = 8 + Math.random() * 6;                          // -8 → -14 px lift
    const rotateAmp = (3 + Math.random() * 3)                    // ±3 → ±6 deg wobble
      * (Math.random() < 0.5 ? -1 : 1);
    const scaleAmp = 1.02 + Math.random() * 0.02;                // 1.02 → 1.04
    const halfDuration = 3 + Math.random() * 0.9;                // each half-cycle 3.0–3.9s

    gsap.to(el, {
      y: -yAmp,
      rotation: rotateAmp,
      scale: scaleAmp,
      duration: halfDuration,
      ease: 'sine.inOut',
      repeat: -1,
      yoyo: true,
    });
  }

  startScrollFly(el, sectionContainer) {
    // Heuristic flight direction from the chip's configured x position:
    // left-of-center chips drift up-and-left, right-of-center drift up-and-right.
    const x = parseFloat(this.dataset.x);
    const signedOffset = (Number.isFinite(x) ? x : 50) - 50;     // -50 … +50
    const horizontalSign = signedOffset === 0 ? 0 : Math.sign(signedOffset);
    const flyX = `${horizontalSign * 8 + signedOffset * 0.12}rem`;
    const flyY = '-13rem';
    const flyRotate = horizontalSign * 22;

    gsap.fromTo(el,
      { x: 0, y: 0, rotation: 0, scale: 1, opacity: 1 },
      {
        x: flyX,
        y: flyY,
        rotation: flyRotate,
        scale: 0.94,
        opacity: 0.94,
        ease: 'none',
        scrollTrigger: {
          trigger: sectionContainer,
          start: 'top top',
          end: 'bottom top',
          scrub: true,
        },
      }
    );
  }
};
